import { z } from 'zod';
import {
  CoverageSchema,
  EMPTY_REASONS,
  ENVIRONMENTS,
  QueryMetaSchema,
  SessionRowSchema,
} from '@zelisight/contract';

/**
 * Response shapes for the read surface listed in plan 5.10.1.
 *
 * The batched query envelope, tiles, metrics, granularities and empty reasons
 * all come from `@zelisight/contract`, which is the single source of truth
 * shared with the collector and the SDK. The shapes declared here are the
 * remaining endpoints, whose bodies the plan describes in prose rather than in
 * the contract package. They are declared once, in one file, and every screen
 * consumes them through `queries.ts`, so a backend change is a single edit
 * here rather than a hunt through components.
 *
 * **These shapes mirror the servers, field for field.** The authorities are
 * `services/api/schema/read_models.py` for everything the read plane answers
 * and the `present()` functions in `services/control/core/` for everything the
 * control plane answers. This file was originally written from the plan's prose
 * rather than from those two, and every place the prose and the code disagreed
 * produced a screen that could only fail: `GET /v1/me` returns its projects as
 * one flat list beside the orgs, not nested inside them, so the dashboard's
 * first authenticated call failed contract validation and no screen could
 * render at all. Where the wire shape and the shape the UI wants differ, the
 * difference is reconciled by a `transform` here, so exactly one place in the
 * app knows about it.
 *
 * Objects are non strict on purpose: Zod strips unknown keys, so the API can
 * add fields without breaking a deployed dashboard.
 */

const EnvironmentSchema = z.enum(ENVIRONMENTS);

/**
 * Empty reasons come from the contract package, never from a copy here.
 *
 * There used to be a hand written list at this spot, four entries long, with a
 * comment claiming it mirrored `read_models.EmptyReason`. That made three
 * independent copies of the same closed set (this one, the contract, and the
 * Python model) and nothing asserted they agreed. Adding a fifth reason server
 * side would have made this `z.enum` reject the whole response, so one new
 * reason on one tile would blank an entire screen rather than render four words
 * of explanation. That is the same failure mode as the `distinct_id` minimum
 * length, and it is why this is now a re-export rather than a list.
 */
export { EMPTY_REASONS };
const EmptyReasonSchema = z.enum(EMPTY_REASONS).nullable();

/**
 * `model_dump()` on the server emits absent optionals as `null`, but a field
 * added to a model after a dashboard build would arrive absent. Accepting both
 * and normalising to `null` keeps a forward deployed dashboard rendering.
 */
const nullableString = z.string().nullable().optional().transform((v) => v ?? null);
const nullableNumber = z.number().nullable().optional().transform((v) => v ?? null);

/** Membership roles, plan FR-AUTH-6. Ordered from most to least capable. */
export const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;
export type Role = (typeof ROLES)[number];
const RoleSchema = z.enum(ROLES);

const ROLE_RANK: Record<Role, number> = { owner: 3, admin: 2, member: 1, viewer: 0 };

/** Plan FR-AUTH-13: a write key is a write capability, so read only roles never see one. */
export function canSeeKeyValue(role: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK.admin;
}

export function canManageProject(role: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK.admin;
}

/* ----------------------------------------------------------------------- *
 * Write keys, shared by /v1/me and the key routes
 * ----------------------------------------------------------------------- */

export const KEY_STATES = ['active', 'deprecated', 'revoked', 'suspended'] as const;
export type KeyState = (typeof KEY_STATES)[number];

export const WriteKeySchema = z.object({
  key_id: z.string(),
  /** Absent for `member` and `viewer` by design, plan FR-AUTH-13. */
  key_value: z.string().optional(),
  key_prefix: z.string(),
  env: EnvironmentSchema,
  state: z.enum(KEY_STATES),
  label: z.string(),
  created_at: z.string(),
  /** Set when the key was superseded by a rotation. */
  deprecated_at: nullableString,
  /** Thirty days after deprecation. Drives the countdown, plan 5.1.1. */
  expires_at: nullableString,
  /** Date granularity only, by design. */
  last_used_date: nullableString,
  last_seen_origins: z.array(z.string()).default([]),
  /** ADR 0035. Domains this key accepts events from; empty means any. */
  allowed_origins: z.array(z.string()).default([]),
});
export type WriteKey = z.infer<typeof WriteKeySchema>;

export const KeysResponseSchema = z.object({
  keys: z.array(WriteKeySchema),
});
export type KeysResponse = z.infer<typeof KeysResponseSchema>;

/**
 * `POST /v1/projects/{id}/keys/{keyId}/rotate` answers with **two** keys, not
 * one: the replacement and the key it deprecated. Both matter to the caller,
 * because the deprecated one keeps working for thirty days (plan 5.1.1) and its
 * expiry drives the countdown on the keys screen.
 */
export const RotateKeyResponseSchema = z.object({
  key: WriteKeySchema,
  deprecated: WriteKeySchema,
});
export type RotateKeyResponse = z.infer<typeof RotateKeyResponseSchema>;

/** `DELETE /keys/<key_id>`. Only a revoked key can be deleted. */
export const DeleteKeyResponseSchema = z.object({
  deleted: z.literal(true),
  key_id: z.string(),
});
export type DeleteKeyResponse = z.infer<typeof DeleteKeyResponseSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/me
 * ----------------------------------------------------------------------- */

/**
 * One project as `/v1/me` returns it: from the org's `PROJ#` listing rows, with
 * the keys this caller's role in that org may see.
 *
 * `first_event_at` and `event_count` live on the project's own `META` row
 * rather than on the listing row, so a control plane that has not yet been
 * redeployed omits them. They are optional here and default to "no event yet",
 * which is the safe reading: it sends a new account to the onboarding screen,
 * where the panel polls `/onboarding` for the truth anyway.
 */
export const ProjectSummarySchema = z.object({
  project_id: z.string().min(1),
  org_id: z.string().min(1),
  name: z.string(),
  state: z.string().default('active'),
  created_at: z.string(),
  role: RoleSchema,
  keys: z.array(WriteKeySchema).default([]),
  first_event_at: nullableString,
  event_count: z.number().int().min(0).optional().transform((v) => v ?? 0),
});
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

/** One org as `orgs.present()` renders it. */
export const OrgSummarySchema = z.object({
  org_id: z.string().min(1),
  name: z.string(),
  slug: z.string(),
  plan: z.string().optional(),
  role: RoleSchema,
  /** Resolved server side. Displayed by the date picker (FR-DASH-2). */
  reporting_timezone: z.string().min(1),
  retention_days: z.number().int().min(1).optional(),
  quota_state: z.string().optional(),
});
export type OrgSummaryWire = z.infer<typeof OrgSummarySchema>;

/** An org with its own projects attached, which is what every screen wants. */
export interface OrgSummary extends OrgSummaryWire {
  projects: ProjectSummary[];
}

export const PendingInviteSchema = z.object({
  org_id: z.string(),
  role: RoleSchema,
  expires_at: nullableString,
});
export type PendingInvite = z.infer<typeof PendingInviteSchema>;

/**
 * The wire shape of `GET /v1/me`, exactly as `services/control/core/me.py`
 * builds it: orgs and projects are two sibling lists joined by `org_id`.
 */
const MeWireSchema = z.object({
  user: z.object({
    sub: z.string().min(1),
    email: z.string(),
    name: z.string().nullable().optional(),
    email_verified: z.boolean().optional(),
  }),
  /** `created`, `existing` or `blocked`. Only the lazy bootstrap sets it. */
  bootstrap: z.string().optional(),
  orgs: z.array(OrgSummarySchema),
  projects: z.array(ProjectSummarySchema).default([]),
  pending_invites: z.array(PendingInviteSchema).default([]),
});

/**
 * The joined view the app uses. Nesting happens here, once, rather than in each
 * of the screens that walks orgs and projects together.
 *
 * A project whose `org_id` matches no org in the response is dropped: it means
 * the membership row that would grant access to that org was not returned, and
 * rendering a project the caller cannot actually query would produce a 404 on
 * every subsequent screen instead of a clean "not found" here.
 */
export const MeSchema = MeWireSchema.transform((wire) => {
  const byOrg = new Map<string, ProjectSummary[]>();
  for (const project of wire.projects) {
    const bucket = byOrg.get(project.org_id);
    if (bucket === undefined) byOrg.set(project.org_id, [project]);
    else bucket.push(project);
  }
  return {
    user: wire.user,
    bootstrap: wire.bootstrap ?? null,
    pending_invites: wire.pending_invites,
    orgs: wire.orgs.map(
      (org): OrgSummary => ({ ...org, projects: byOrg.get(org.org_id) ?? [] }),
    ),
  };
});
export type Me = z.infer<typeof MeSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}/live
 * ----------------------------------------------------------------------- */

/**
 * Mirrors `read_models.LiveEvent`. The collector stores the whole accepted
 * event and the feed returns it under `raw`, so the expandable panel renders
 * the event as it was received rather than a reconstruction of it.
 */
export const LiveEventSchema = z.object({
  /** Opaque per event position. The newest one is what the next poll passes back. */
  cursor: z.string(),
  event_id: z.string(),
  name: z.string(),
  ts: z.string(),
  received_at: nullableString,
  distinct_id: nullableString,
  /** Resolved server side. Null for the anonymous majority of a live feed. */
  display_name: nullableString,
  session_id: nullableString,
  url_host: nullableString,
  url_path: nullableString,
  is_bot: z.boolean().nullable().optional().transform((v) => v ?? false),
  raw: z.record(z.string(), z.unknown()).default({}),
});
export type LiveEvent = z.infer<typeof LiveEventSchema>;

export const LiveResponseSchema = z.object({
  meta: QueryMetaSchema,
  events: z.array(LiveEventSchema),
  /** Opaque. Pass back as `after` to fetch only what arrived since. */
  cursor: nullableString,
  /** While set, the collector is still writing live rows for this project. */
  live_until: nullableString,
  empty_reason: EmptyReasonSchema,
});
export type LiveResponse = z.infer<typeof LiveResponseSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}/events/{name}
 * ----------------------------------------------------------------------- */

export const BreakdownValueSchema = z.object({
  value: z.string(),
  events: z.number().min(0),
});

/** Mirrors `read_models.BreakdownResult`. The property name is `key`. */
export const BreakdownSchema = z.object({
  key: z.string(),
  /** The property was suppressed as personally identifying (plan 5.7). */
  suppressed: z.boolean().default(false),
  /** True when the property exceeded the cardinality cap and was bucketed. */
  truncated: z.boolean().default(false),
  values: z.array(BreakdownValueSchema),
});
export type Breakdown = z.infer<typeof BreakdownSchema>;

/**
 * Mirrors `read_models.DrilldownResponse`.
 *
 * The approximation disclosure applies to `unique_persons` only: the event
 * count is a counter and is exact, the person count comes from a HyperLogLog
 * union. There is no `available_properties` field here, because the breakdown
 * typeahead is its own endpoint (`/events/{name}/properties`), which is what
 * lets it carry the per key `suppressed` flag that the drilldown does not.
 */
export const EventDetailSchema = z.object({
  meta: QueryMetaSchema,
  event_name: z.string(),
  total_events: z.number().min(0),
  unique_persons: z.number().min(0),
  approximate: z.boolean().optional(),
  error_pct: z.number().min(0).optional(),
  method: z.string().optional(),
  points: z.array(z.object({ t: z.string(), v: z.number() })),
  breakdowns: z.array(BreakdownSchema),
  empty_reason: EmptyReasonSchema,
});
export type EventDetail = z.infer<typeof EventDetailSchema>;

/** Mirrors `read_models.PropertyKeysResponse`, the breakdown typeahead. */
export const PropertyKeySchema = z.object({
  key: z.string(),
  suppressed: z.boolean().default(false),
  distinct_values_seen: z.number().int().min(0).default(0),
});
export type PropertyKey = z.infer<typeof PropertyKeySchema>;

export const PropertyKeysResponseSchema = z.object({
  meta: QueryMetaSchema,
  event_name: z.string(),
  keys: z.array(PropertyKeySchema),
});
export type PropertyKeysResponse = z.infer<typeof PropertyKeysResponseSchema>;

/* ----------------------------------------------------------------------- *
 * People
 * ----------------------------------------------------------------------- */

/**
 * `first_seen` and `last_seen` are nullable on the server: a person row written
 * by an identify call that arrived before any event has neither yet. Requiring
 * them here rejected the whole page for one such row.
 */
export const PersonRowSchema = z.object({
  distinct_id: z.string(),
  /** Resolved server side through the chain in zs_core/identity.py. */
  display_name: nullableString,
  email: nullableString,
  first_seen: nullableString,
  last_seen: nullableString,
  event_count: z.number().int().min(0).default(0),
});
export type PersonRow = z.infer<typeof PersonRowSchema>;

export const PeopleResponseSchema = z.object({
  meta: QueryMetaSchema,
  rows: z.array(PersonRowSchema),
  cursor: nullableString,
  empty_reason: EmptyReasonSchema,
});
export type PeopleResponse = z.infer<typeof PeopleResponseSchema>;

export const PersonDetailSchema = z.object({
  meta: QueryMetaSchema,
  display_name: nullableString,
  distinct_id: z.string(),
  props: z.record(z.string(), z.unknown()).default({}),
  anon_ids: z.array(z.string()).default([]),
  first_seen: nullableString,
  last_seen: nullableString,
  event_count: z.number().int().min(0).default(0),
  /** Bumped by each identity stitch, so a merged person is visibly merged. */
  identity_generation: z.number().int().min(0).default(0),
  deletion_requested_at: nullableString,
});
export type PersonDetail = z.infer<typeof PersonDetailSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}/people/{did}/timeline
 * ----------------------------------------------------------------------- */

/** Mirrors `read_models.TimelineRow`. */
export const TimelineEventSchema = z.object({
  event_id: z.string(),
  name: z.string(),
  ts: z.string(),
  session_id: nullableString,
  url_host: nullableString,
  url_path: nullableString,
  props: z.record(z.string(), z.unknown()).default({}),
  /** True for a row served from the live feed rather than from Parquet. */
  live: z.boolean().default(false),
});
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

/**
 * Mirrors `read_models.TimelineResponse`. The rows are events only; sessions
 * are a separate endpoint, because the timeline is DuckDB over Parquet and the
 * session list is a DynamoDB range read, and unioning them server side would
 * have made the fast one wait for the slow one.
 */
export const PersonTimelineSchema = z.object({
  meta: QueryMetaSchema,
  distinct_id: z.string(),
  rows: z.array(TimelineEventSchema),
  cursor: nullableString,
  empty_reason: EmptyReasonSchema,
});
export type PersonTimeline = z.infer<typeof PersonTimelineSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}/sessions
 * ----------------------------------------------------------------------- */

/** Mirrors `read_models.SessionRow`, shared with the batched `sessions` tile. */
export const PersonSessionSchema = z.object({
  session_id: z.string(),
  distinct_id: z.string().optional(),
  started_at: z.string(),
  duration_s: z.number().min(0),
  event_count: z.number().int().min(0),
  has_replay: z.boolean(),
});
export type PersonSession = z.infer<typeof PersonSessionSchema>;

/**
 * Bounce rate and session duration for a whole range.
 *
 * A separate call from the session list, because the list pages: a bounce rate
 * derived from the rows on screen describes the first page and presents it as a
 * measurement of the range. This is a cold scan over every session, so it is
 * asked for once per range rather than once per page of the list.
 */
export const SessionMetricsSchema = z.object({
  meta: QueryMetaSchema,
  sessions: z.number().int().min(0),
  bounced: z.number().int().min(0),
  bounce_rate_pct: z.number(),
  avg_duration_s: z.number(),
  median_duration_s: z.number(),
  avg_engaged_duration_s: z.number(),
  median_engaged_duration_s: z.number(),
  coverage: CoverageSchema,
});
export type SessionMetrics = z.infer<typeof SessionMetricsSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}/attribution
 * ----------------------------------------------------------------------- */

/**
 * One value of one breakdown. Mirrors `read_models.AttributionRow`.
 *
 * `value` is the empty string when the dimension was absent on that visit: no
 * `$utm_source` on the entry pageview, or no referrer at all. Those rows are
 * returned rather than dropped, because the share of traffic carrying no
 * campaign at all is the number the screen exists to show.
 */
export const AttributionRowSchema = z.object({
  value: z.string(),
  sessions: z.number().int().min(0),
  /** Counted once per value, so these do NOT sum to the range total. */
  persons: z.number().int().min(0),
  events: z.number().int().min(0),
});
export type AttributionRow = z.infer<typeof AttributionRowSchema>;

export const AttributionBreakdownSchema = z.object({
  rows: z.array(AttributionRowSchema),
  /** Distinct values before the cap, so a card can say "top 20 of 340". */
  value_count: z.number().int().min(0),
  /** Sessions across every value including the ones the cap dropped, which is
   *  the only honest denominator for a share computed from the rows shown. */
  sessions: z.number().int().min(0),
});
export type AttributionBreakdown = z.infer<typeof AttributionBreakdownSchema>;

/**
 * Where a range's visits came from.
 *
 * The attribution semantics live in
 * `services/query/core/sql/campaign_attribution.sql` and the two that change how
 * these numbers read are worth repeating at the point somebody renders them: the
 * unit is a SESSION rather than a tagged event, and a session is credited to the
 * FIRST campaign tag seen inside it rather than the last.
 *
 * `sessions`, `persons` and `events` are the range totals, measured rather than
 * summed from the breakdowns. Summing would overcount persons, because a person
 * who arrives from two channels is a person in both rows.
 */
export const AttributionSchema = z.object({
  meta: QueryMetaSchema,
  sessions: z.number().int().min(0),
  persons: z.number().int().min(0),
  events: z.number().int().min(0),
  rows_per_dimension: z.number().int().min(0),
  source: AttributionBreakdownSchema,
  medium: AttributionBreakdownSchema,
  campaign: AttributionBreakdownSchema,
  channel: AttributionBreakdownSchema,
  referrer: AttributionBreakdownSchema,
  coverage: CoverageSchema,
});
export type Attribution = z.infer<typeof AttributionSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}/audience
 * ----------------------------------------------------------------------- */

/**
 * Who a range's visits were. Every breakdown is one value per visit and reuses
 * the attribution breakdown shape; an empty `value` means the dimension could
 * not be identified for that visit. How each value is read is argued in
 * `services/query/core/sql/audience.sql`.
 */
export const AudienceSchema = z.object({
  meta: QueryMetaSchema,
  sessions: z.number().int().min(0),
  persons: z.number().int().min(0),
  events: z.number().int().min(0),
  rows_per_dimension: z.number().int().min(0),
  /** ISO 3166 alpha-2, stamped at the edge. */
  country: AttributionBreakdownSchema,
  /** `desktop`, `mobile` or `tablet`. */
  device_type: AttributionBreakdownSchema,
  browser: AttributionBreakdownSchema,
  os: AttributionBreakdownSchema,
  device: AttributionBreakdownSchema,
  /** `WIDTHxHEIGHT` in CSS pixels. */
  screen: AttributionBreakdownSchema,
  language: AttributionBreakdownSchema,
  timezone: AttributionBreakdownSchema,
  landing_page: AttributionBreakdownSchema,
  exit_page: AttributionBreakdownSchema,
  coverage: CoverageSchema,
});
export type Audience = z.infer<typeof AudienceSchema>;
export type AudienceDimension = Exclude<
  keyof Audience,
  'meta' | 'sessions' | 'persons' | 'events' | 'rows_per_dimension' | 'coverage'
>;

/**
 * `GET /v1/projects/{id}/sessions`. The rows are the read plane's full
 * `SessionRow`, the same shape the sessions tile returns, so the paged list can
 * show the person, the landing page and whether the session is still open.
 * Parsing it with the narrower PersonSessionSchema used to strip those fields.
 */
export const SessionListResponseSchema = z.object({
  meta: QueryMetaSchema,
  rows: z.array(SessionRowSchema),
  cursor: nullableString,
  empty_reason: EmptyReasonSchema,
});
export type SessionListResponse = z.infer<typeof SessionListResponseSchema>;

/* ----------------------------------------------------------------------- *
 * Session replay
 * ----------------------------------------------------------------------- */

export const ReplayChunkSchema = z.object({
  sequence: z.number().int().min(0),
  url: z.string(),
  created_at: nullableString,
});

export const ReplayManifestSchema = z.object({
  session_id: z.string(),
  project_id: z.string(),
  chunk_count: z.number().int().min(0),
  expires_in: z.number().int().min(0),
  /** The cursor for the next poll: the highest sequence in this response. */
  latest_sequence: z.number().int().min(0).nullable().default(null),
  /**
   * When the newest chunk was signed, or null when the poll found nothing new.
   *
   * The server deliberately reports a timestamp rather than a `live` boolean.
   * How stale is too stale is a judgement, and it differs between a badge in a
   * list and a player that is mid playback, so the caller decides.
   */
  latest_chunk_at: nullableString,
  chunks: z.array(ReplayChunkSchema),
});
export type ReplayManifest = z.infer<typeof ReplayManifestSchema>;

/* ----------------------------------------------------------------------- *
 * Onboarding
 * ----------------------------------------------------------------------- */

/** Mirrors `read_models.OnboardingResponse`. */
export const OnboardingSchema = z.object({
  project_id: z.string().optional(),
  /** Null until the first event has been received. */
  first_event_at: nullableString,
  /** The raw event as stored, capped server side. Rendered verbatim (A3.5). */
  first_event_json: z.unknown().nullable().optional().transform((v) => v ?? null),
  /** `received_at` minus `ts` for that first event. */
  latency_ms: nullableNumber,
  event_count: z.number().int().min(0).default(0),
  event_name_count: z.number().int().min(0).default(0),
  /** True while the collector's live tap is firing, so the panel can say so. */
  tap_open: z.boolean().default(false),
  as_of: z.string().optional(),
});
export type Onboarding = z.infer<typeof OnboardingSchema>;

/* ----------------------------------------------------------------------- *
 * GET /v1/projects/{id}
 * ----------------------------------------------------------------------- */

/**
 * Mirrors `services/control/core/projects.py::present`.
 *
 * Retention and the reporting timezone are **not** here. They are org level
 * settings (plan D30 puts the reporting timezone on the org so that "yesterday"
 * cannot mean two things inside one organisation), they are returned by
 * `/v1/me` on the org, and the settings screen reads them from the workspace.
 */
export const ProjectSettingsSchema = z.object({
  project_id: z.string(),
  org_id: z.string(),
  name: z.string(),
  state: z.string().default('active'),
  created_at: z.string(),
  rate_limit_epm: z.number().int().min(0),
  quota_state: z.string().default('ok'),
  /** On unless an admin turned it off; the control plane sends the effective value. */
  replay_enabled: z.boolean().default(true),
  replay_sample_rate: nullableNumber,
  /** ADR 0034. Click, scroll and snapshot capture for the Heatmaps screen. On
   *  unless an admin turned it off, matching the collector. */
  heatmaps_enabled: z.boolean().default(true),
  person_properties_writable: z.boolean().default(true),
  first_event_at: nullableString,
  event_count: z.number().int().min(0).default(0),
  event_name_count: z.number().int().min(0).default(0),
  active_key_count: z.number().int().min(0).nullable().optional().transform((v) => v ?? 0),
  suspended_at: nullableString,
  suspended_reason: nullableString,
});
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;
